/**
 * Client-side half of the plan gate.
 *
 * The server answers 402 when professional workspace or team collaboration
 * access is missing (src/routes/plan-gate.ts). The React app
 * calls fetch() from dozens of pages with no shared wrapper, so rather than
 * touching every call site we wrap window.fetch once and broadcast a
 * `cmcp:plan-required` CustomEvent whenever such a response comes back.
 * PlanRequiredBanner listens and shows the upgrade prompt.
 */
export const PLAN_REQUIRED_EVENT = "cmcp:plan-required";

export interface PlanRequiredDetail {
  code: "plan_required" | "team_plan_required" | "plan_activation_required";
  message: string;
  upgrade_url: string;
  method: string;
  url: string;
}

let installed = false;

function isPlanRequiredBody(body: unknown): body is { error: PlanRequiredDetail["code"]; message?: string; upgrade_url?: string } {
  return !!body && typeof body === "object" && ["plan_required", "team_plan_required", "plan_activation_required"].includes(String((body as { error?: unknown }).error));
}

export function installPlanRequiredInterceptor(): void {
  if (installed || typeof window === "undefined" || typeof window.fetch !== "function") return;
  installed = true;
  const original = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const response = await original(input, init);
    if (response.status !== 402) return response;
    const method = (init?.method ?? (input instanceof Request ? input.method : "GET")).toUpperCase();
    try {
      const body = await response.clone().json();
      if (isPlanRequiredBody(body)) {
        // Feature-specific reads explain their own plan boundary. A pending
        // account returning to the app needs the checkout path on reads too.
        if (body.error !== "plan_activation_required" && !["POST", "PUT", "PATCH", "DELETE"].includes(method)) return response;
        const url = typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;
        const detail: PlanRequiredDetail = {
          code: body.error,
          message: typeof body.message === "string" ? body.message : body.error === "plan_activation_required"
            ? "Choose a plan to activate your account." : body.error === "team_plan_required"
            ? "Team collaboration requires Platform & Teams or an existing team-enabled contract."
            : "This feature requires a professional workspace plan. Explorer includes personal research and tracking.",
          upgrade_url: body.upgrade_url ?? "/start",
          method,
          url,
        };
        window.dispatchEvent(new CustomEvent<PlanRequiredDetail>(PLAN_REQUIRED_EVENT, { detail }));
      }
    } catch {
      // Non-JSON 402 — not ours; leave the response untouched.
    }
    return response;
  };
}
